import React from 'react'
import { useApp } from '../context/AppContext'
import { Appointment, OpeningWindow, TimeBlock, TimeOff, api } from '../api/client'
import { screenCache } from '../api/screenCache'
import { addDays, addMonths, formatLongDay, formatMonth, formatWeekRange, monthGridRange, todayIso, weekStart } from '../calendar/dates'
import type { CalendarView } from '../types/app'
import DayView from './calendar/DayView'
import MonthView from './calendar/MonthView'
import WeekView from './calendar/WeekView'
import './Calendar.css'
import { IconButton } from '../ui/Icon'
import { Loading } from '../ui/States'

interface CalendarData {
  range: string
  appointments: Appointment[]
  windows: OpeningWindow[]
  timeOff: TimeOff[]
  // Missing from an entry cached before blocks existed.
  timeBlocks: TimeBlock[]
}

const VIEWS: { view: CalendarView; label: string }[] = [
  { view: 'day', label: 'Day' },
  { view: 'week', label: 'Week' },
  { view: 'month', label: 'Month' },
]

function viewRange(view: CalendarView, date: string): { from: string; to: string } {
  if (view === 'day') return { from: date, to: date }
  if (view === 'month') return monthGridRange(date)
  const from = weekStart(date)
  return { from, to: addDays(from, 6) }
}

export default function Calendar() {
  const { state, dispatch } = useApp()
  const date = state.calendarDate
  const view = state.calendarView
  const { from, to } = viewRange(view, date)
  const range = `${from}/${to}`

  const [data, setData] = React.useState<CalendarData | null>(() => screenCache.get<CalendarData>(range) ?? null)
  const [failed, setFailed] = React.useState(false)
  const latest = React.useRef(0)

  // A cached range is shown at once, and a failed background refresh just
  // keeps it: the status pane below only appears when there's nothing to show.
  const load = React.useCallback(async () => {
    const request = ++latest.current
    const cached = screenCache.get<CalendarData>(range)
    if (cached) setData(cached)
    try {
      const [appointments, windows, timeOff, timeBlocks] = await Promise.all([
        api.listAppointments(from, to),
        api.getWorkingHours(),
        api.listTimeOff(),
        api.listTimeBlocks(from, to),
      ])
      if (request === latest.current) {
        const next = { range: `${from}/${to}`, appointments, windows, timeOff, timeBlocks }
        setData(next)
        screenCache.set(range, next)
        setFailed(false)
      }
    } catch {
      if (request === latest.current) {
        setFailed(true)
      }
    }
  }, [from, to, range])

  React.useEffect(() => {
    load()
  }, [load, state.calendarVersion])

  // A home-screen PWA left open overnight still shows the day it was on when
  // last visible; if that day was "today" back then, follow the date forward.
  const lastToday = React.useRef(todayIso())
  const calendarDateRef = React.useRef(state.calendarDate)
  calendarDateRef.current = state.calendarDate

  // Bookings made on another device show up when the app comes back.
  React.useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        const now = todayIso()
        if (now !== lastToday.current && calendarDateRef.current === lastToday.current) {
          dispatch({ type: 'SET_CALENDAR_DATE', payload: now })
        }
        lastToday.current = now
        load()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [load, dispatch])

  const today = todayIso()
  const go = (next: string) => dispatch({ type: 'SET_CALENDAR_DATE', payload: next })
  const move = (by: number) => go(view === 'month' ? addMonths(date, by) : addDays(date, view === 'week' ? by * 7 : by))
  const title = view === 'day' ? formatLongDay(date) : view === 'week' ? formatWeekRange(from) : formatMonth(date)
  // Data for another date or view is never shown as this one's.
  const current = data !== null && data.range === range ? data : null

  return (
    <div className={`screen calendar-screen${view === 'day' ? '' : ' calendar-screen--wide'}`}>
      <div className="calendar-header">
        <div className="calendar-nav">
          <IconButton icon="chevron-left" variant="outlined" className="calendar-arrow" label={`Previous ${view}`} onClick={() => move(-1)} />
          <h1>{title}</h1>
          <IconButton icon="chevron-right" variant="outlined" className="calendar-arrow" label={`Next ${view}`} onClick={() => move(1)} />
        </div>
        <div className="calendar-controls">
          {date !== today && (
            <button type="button" className="btn btn-secondary btn-compact" onClick={() => go(today)}>
              Today
            </button>
          )}
          <div className="segmented" role="group" aria-label="View">
            {VIEWS.map((v) => (
              <button
                key={v.view}
                type="button"
                aria-pressed={view === v.view}
                onClick={() => dispatch({ type: 'SET_CALENDAR_VIEW', payload: v.view })}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {!current ? (
        failed ? (
          <div className="calendar-status" role="alert">
            <p>Couldn’t load your calendar. Check your connection and try again.</p>
            <button type="button" className="btn btn-secondary btn-compact" onClick={load}>
              Try again
            </button>
          </div>
        ) : (
          <Loading />
        )
      ) : view === 'day' ? (
        <DayView date={date} appointments={current.appointments} windows={current.windows} timeOff={current.timeOff} timeBlocks={current.timeBlocks ?? []} />
      ) : view === 'week' ? (
        <WeekView date={date} today={today} appointments={current.appointments} windows={current.windows} timeOff={current.timeOff} timeBlocks={current.timeBlocks ?? []} />
      ) : (
        <MonthView date={date} today={today} appointments={current.appointments} windows={current.windows} timeOff={current.timeOff} />
      )}
    </div>
  )
}
